import { CheckOutlined } from '@mui/icons-material'
import { Tooltip, Typography, TypographyProps } from '@mui/material'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { ProjectStateAsString } from 'controllers/Project/ProjectStateMachine'
import { usePartyDateFormat } from 'customHooks/usePartyDateTimeFormat'
import { ExpirationDateProgressVariant } from 'features/BillOfMaterials/QuotationExpirationDate/ExpirationDateProgressVariant'
import {
  MRT_Cell,
  MRT_Column,
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableInstance,
} from 'material-react-table'
import { MoneyString } from 'model/Money'
import moment from 'moment'
import { ReactNode, RefObject, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { ProjectSummaryDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { markTextWithSpacesComponent } from 'utils/textFilterUtils'

const statesToRenderExpirationDate: ProjectStateAsString[] = [
  ProjectStateAsString.CANCELLED,
  ProjectStateAsString.DEFINING,
  ProjectStateAsString.REQUESTED,
  ProjectStateAsString.QUOTING,
  ProjectStateAsString.QUOTED,
  ProjectStateAsString.NEGOTIATING,
]

const _DefaultTypography = (props: {
  cell: MRT_Cell<ProjectSummaryDto, unknown>
  column: MRT_Column<ProjectSummaryDto, unknown>
  renderedCellValue: ReactNode
  row: MRT_Row<ProjectSummaryDto>
  rowRef?: RefObject<HTMLTableRowElement | null>
  staticColumnIndex?: number
  staticRowIndex?: number
  table: MRT_TableInstance<ProjectSummaryDto>
  typographyProps?: TypographyProps<'a' | 'span'>
}) => {
  return (
    <Tooltip title={props.renderedCellValue}>
      <Typography
        variant="body2"
        whiteSpace={'pre-wrap'}
        style={{
          overflow: 'hidden',
          maxWidth: '9vw',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          // textAlign: 'right',
        }}
        {...(props.typographyProps || {})}
      >
        {markTextWithSpacesComponent(
          props.cell.getValue() as string,
          props.table.getState().globalFilter,
        )}
      </Typography>
    </Tooltip>
  )
}

export const useProjectListColumns = () => {
  const { t } = useTranslation()

  const dateFormat = usePartyDateFormat()
  const navigate = useNavigate()

  const columns: MRT_ColumnDef<ProjectSummaryDto>[] = useMemo(() => {
    return [
      {
        size: 100,
        minSize: 50,
        id: 'lastOperation',
        header: t('common:last-modified'),
        Header: <LocalizedTypography translationKey="common:last-modified" />,
        sortDescFirst: true,
        accessorFn: (row) =>
          moment(row.lastOperation, 'YYYY-MM-DD').format(dateFormat),
        Cell: (cellProps) => {
          return (
            <Typography variant="body2">
              {moment(cellProps.row.original.lastOperation).format(dateFormat)}
            </Typography>
          )
        },
        enableClickToCopy: false,
      },

      {
        size: 100,
        minSize: 20,
        id: 'status',
        Header: <LocalizedTypography translationKey="common:status" />,
        accessorFn: (row) => row.status,
        header: t('common:status'),
        Cell: (_props) => {
          return (
            <LocalizedTypography
              translationKey={`project:${_props.row.original.status}`}
            />
          )
        },
        enableClickToCopy: false,
        enableSorting: true,
      },
      {
        size: 150,
        minSize: 20,
        id: 'orderNumber',
        Header: <LocalizedTypography translationKey="common:order-number" />,
        accessorFn: (row) => row.orderNumber,
        header: t('common:order-number'),
        enableGrouping: false,
        muiTableBodyCellProps: {
          align: 'left',
        },
        Cell: _DefaultTypography,
        enableClickToCopy: false,
        enableSorting: true,
      },
      {
        size: 150,
        minSize: 20,
        id: 'projectReference',
        Header: (
          <LocalizedTypography translationKey="common:project-reference" />
        ),
        accessorFn: (row) => row.projectReference,
        header: t('common:project-reference'),
        enableGrouping: false,
        Cell: (renderProps) => {
          return (
            <_DefaultTypography
              cell={renderProps.cell}
              column={renderProps.column}
              renderedCellValue={renderProps.renderedCellValue}
              row={renderProps.row}
              table={renderProps.table}
              typographyProps={{
                component: 'a',
                onClick: (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  navigate(`/app/project/${renderProps.row.original.id}`)
                },
                href: `app/project/${renderProps.row.original.id}`,
                style: {
                  textDecoration: 'none',
                  color: 'inherit',
                },
              }}
            />
          )
        },
        visibleInShowHideMenu: false,
        enableClickToCopy: false,
        enableSorting: true,
      },
      {
        size: 100,
        minSize: 50,
        id: 'isCreatedInPortal',
        Header: (
          <LocalizedTypography translationKey="common:created-in-portal" />
        ),
        accessorFn: (row) => row.isCreatedInPortal,
        header: t('common:created-in-portal'),
        enableClickToCopy: false,
        muiTableBodyCellProps: {
          align: 'center',
        },
        Cell: (cellProps) => {
          if (cellProps.row.original.isCreatedInPortal) {
            return <CheckOutlined fontSize="small" />
          } else {
            return null
          }
        },
      },
      {
        size: 80,
        minSize: 20,
        id: 'numberOfRows',
        Header: <LocalizedTypography translationKey="common:num-parts" />,
        enableGrouping: false,
        accessorFn: (row) => row.numberOfRows,
        header: t('common:num-parts'),
        muiTableBodyCellProps: {
          align: 'right',
          sx: {
            paddingRight: '2rem',
          },
        },
        Cell: _DefaultTypography,
        enableClickToCopy: false,
      },
      {
        size: 120,
        minSize: 20,
        id: 'salesPriceOfItemsAmount', //.toLowerCase(),
        Header: (
          <LocalizedTypography translationKey="common:sales-price-of-items" />
        ),
        muiTableBodyCellProps: {
          align: 'right',
          sx: {
            paddingRight: '2rem',
          },
        },
        accessorFn: (row) => MoneyString(row.salesPriceOfItems),
        header: t('common:sales-price-of-items'),
        enableGrouping: false,
        Cell: _DefaultTypography,
        enableClickToCopy: false,
      },
      {
        size: 100,
        minSize: 20,
        id: 'quotationExpireDate',
        enableSorting: true,
        sortDescFirst: true,
        Header: <LocalizedTypography translationKey="common:expires" />,
        header: t('common:expires'),
        accessorFn: (row) => row.quotationExpiresAt,
        muiTableBodyCellProps: {
          align: 'center',
          sx: {
            paddingBottom: 0,
          },
        },
        Cell: (rowData) => {
          if (!rowData.row.original.quotationExpiresAt) {
            return null
          }

          if (
            !statesToRenderExpirationDate.includes(
              rowData.row.original.status.toUpperCase() as ProjectStateAsString,
            )
          ) {
            return null
          }

          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
              }}
            >
              <ExpirationDateProgressVariant
                expirationDate={rowData.row.original.quotationExpiresAt}
                disableLabel
              />
            </div>
          )
        },
        enableClickToCopy: false,
      },
      {
        size: 150,
        minSize: 100,
        id: 'buyingPartyName', //.toLowerCase(),
        Header: <LocalizedTypography translationKey="common:customer" />,
        accessorFn: (row) => row.buyingPartyName,
        header: t('common:customer'),
        enableGrouping: true,
        Cell: _DefaultTypography,
        enableClickToCopy: true,
      },
      {
        size: 100,
        minSize: 40,
        id: 'contactPersonEmail', //.toLowerCase(),
        Header: <LocalizedTypography translationKey="common:contact-person" />,
        accessorFn: (row) => row.contactPerson?.email,
        header: t('common:contact-person'),
        Cell: _DefaultTypography,
        enableClickToCopy: true,
      },
      {
        size: 100,
        minSize: 40,
        id: 'sellingPartyName', //.toLowerCase(),
        Header: <LocalizedTypography translationKey="common:supplier" />,
        accessorFn: (row) => row.sellingPartyName,
        header: t('common:supplier'),
        Cell: _DefaultTypography,
        enableClickToCopy: true,
      },
      {
        size: 100,
        minSize: 40,
        id: 'salesPersonEmail', //.toLowerCase(),
        Header: (
          <LocalizedTypography translationKey="common:sales-representative" />
        ),
        accessorFn: (row) => row.salesPerson?.email,
        header: t('common:sales-representative'),
        Cell: _DefaultTypography,
        enableClickToCopy: true,
      },
      {
        size: 100,
        minSize: 40,
        id: 'creatorEmail',
        Header: <LocalizedTypography translationKey="common:created-by" />,
        accessorFn: (row) => row.creator?.email,
        header: t('common:created-by'),
        Cell: _DefaultTypography,
        enableClickToCopy: true,
      },
      {
        size: 100,
        minSize: 40,
        id: 'isCreatedOn',
        Header: <LocalizedTypography translationKey="common:created-at" />,
        header: t('common:created-at'),
        accessorFn: (row) =>
          moment(row.isCreatedOn, 'YYYY-MM-DD').format(dateFormat),
        Cell: (cellProps) => {
          return (
            <Typography variant="body2">
              {moment(cellProps.row.original.isCreatedOn).format(dateFormat)}
            </Typography>
          )
        },
      },
    ] satisfies MRT_ColumnDef<ProjectSummaryDto>[]
  }, [dateFormat, navigate, t])

  return columns
}
